/**
 * Feedback blocks: process, timeline, faq, testimonial, alert
 * Blocks that display processes, feedback, and informational content.
 */

import { registerBlockRenderer, getBlockRendererContext } from './registry';
import { escapeHtml, createBlockWrapper } from './utils';
import { iconCheck, iconWarning, iconInfo, iconCelebrate, iconChevronRight, iconStar, iconStarEmpty } from '../icons';
import type { ProcessData, TimelineData, FaqData, TestimonialData, AlertData } from './types';

// =============================================================================
// PROCESS BLOCK
// =============================================================================

registerBlockRenderer('process', (data: unknown, container: HTMLElement) => {
  const processData = data as ProcessData;
  const steps = processData.steps ?? [];

  const wrapper = createBlockWrapper('process');

  const stepsWrapper = document.createElement('div');
  stepsWrapper.className = 'pms-steps';

  steps.forEach((step, index) => {
    const stepEl = document.createElement('div');
    const status = step.status ?? 'upcoming';
    stepEl.className = `pms-step pms-step-${status}`;

    const number = index + 1;
    let html = `<div class="pms-step-number">${number}</div>`;
    html += '<div class="pms-step-content">';
    if (step.title) {
      html += `<div class="pms-step-title">${escapeHtml(step.title)}</div>`;
    }
    if (step.description) {
      html += `<div class="pms-step-description">${escapeHtml(step.description)}</div>`;
    }
    html += '</div>';

    stepEl.innerHTML = html;
    stepsWrapper.appendChild(stepEl);
  });

  wrapper.appendChild(stepsWrapper);
  container.appendChild(wrapper);
});

// =============================================================================
// TIMELINE BLOCK
// =============================================================================

registerBlockRenderer('timeline', (data: unknown, container: HTMLElement) => {
  const timelineData = data as TimelineData & { events?: TimelineData['items'] };
  // Support both items and events field names (backend uses events)
  const items = timelineData.items ?? timelineData.events ?? [];

  const wrapper = createBlockWrapper('timeline');

  const timeline = document.createElement('div');
  timeline.className = 'pms-timeline';

  for (const item of items) {
    const status = item.status ?? 'upcoming';
    const itemEl = document.createElement('div');
    itemEl.className = `pms-timeline-item pms-timeline-${status}`;

    let html = '<div class="pms-timeline-dot"></div>';
    html += '<div class="pms-timeline-content">';
    html += `<div class="pms-timeline-date">${escapeHtml(item.date)}</div>`;
    html += `<div class="pms-timeline-title">${escapeHtml(item.title)}</div>`;
    if (item.description) {
      html += `<div class="pms-timeline-description">${escapeHtml(item.description)}</div>`;
    }
    html += '</div>';

    itemEl.innerHTML = html;
    timeline.appendChild(itemEl);
  }

  wrapper.appendChild(timeline);
  container.appendChild(wrapper);
});

// =============================================================================
// FAQ BLOCK
// =============================================================================

registerBlockRenderer('faq', (data: unknown, container: HTMLElement) => {
  const faqData = data as FaqData;
  const items = faqData.items ?? [];

  const wrapper = createBlockWrapper('faq');

  if (faqData.title) {
    const title = document.createElement('div');
    title.className = 'pms-faq-title';
    title.textContent = faqData.title;
    wrapper.appendChild(title);
  }

  const accordion = document.createElement('div');
  accordion.className = 'pms-faq-accordion';

  for (const item of items) {
    const faqItem = document.createElement('div');
    faqItem.className = 'pms-faq-item';

    const question = document.createElement('button');
    question.className = 'pms-faq-question';
    question.innerHTML = `<span>${escapeHtml(item.question)}</span><span class="pms-faq-chevron">${iconChevronRight()}</span>`;

    const answer = document.createElement('div');
    answer.className = 'pms-faq-answer';
    answer.innerHTML = `<div class="pms-faq-answer-content">${escapeHtml(item.answer)}</div>`;

    question.addEventListener('click', () => {
      const isOpen = faqItem.classList.contains('pms-faq-item-open');

      // Close all other items
      accordion.querySelectorAll('.pms-faq-item-open').forEach(el => {
        el.classList.remove('pms-faq-item-open');
      });

      if (!isOpen) {
        faqItem.classList.add('pms-faq-item-open');
      }
    });

    faqItem.appendChild(question);
    faqItem.appendChild(answer);
    accordion.appendChild(faqItem);
  }

  wrapper.appendChild(accordion);
  container.appendChild(wrapper);
});

// =============================================================================
// TESTIMONIAL BLOCK
// =============================================================================

registerBlockRenderer('testimonial', (data: unknown, container: HTMLElement) => {
  const testimonialData = data as TestimonialData & { quote?: string; author?: string; role?: string; company?: string; avatar?: string; rating?: number };

  // Support both array format (widget) and single object format (backend)
  let items: TestimonialData['items'];
  if (testimonialData.items && testimonialData.items.length > 0) {
    items = testimonialData.items;
  } else if (testimonialData.quote && testimonialData.author) {
    // Backend sends single testimonial object - wrap in array
    items = [{
      quote: testimonialData.quote,
      author: testimonialData.author,
      role: testimonialData.role,
      company: testimonialData.company,
      rating: testimonialData.rating
    }];
  } else {
    items = [];
  }

  const wrapper = createBlockWrapper('testimonial');

  const testimonials = document.createElement('div');
  testimonials.className = 'pms-testimonials';

  for (const item of items) {
    const testimonial = document.createElement('div');
    testimonial.className = 'pms-testimonial-item';

    let html = '';

    // Star rating
    if (item.rating !== undefined && item.rating > 0) {
      html += '<div class="pms-testimonial-rating">';
      for (let i = 0; i < 5; i++) {
        html += `<span class="pms-star ${i < item.rating ? 'pms-star-filled' : ''}">${i < item.rating ? iconStar() : iconStarEmpty()}</span>`;
      }
      html += '</div>';
    }

    html += `<div class="pms-testimonial-quote">"${escapeHtml(item.quote)}"</div>`;
    html += '<div class="pms-testimonial-author">';
    html += `<span class="pms-testimonial-name">${escapeHtml(item.author)}</span>`;
    if (item.role || item.company) {
      const attribution = [item.role, item.company].filter(Boolean).join(', ');
      html += `<span class="pms-testimonial-role">${escapeHtml(attribution)}</span>`;
    }
    html += '</div>';

    testimonial.innerHTML = html;
    testimonials.appendChild(testimonial);
  }

  wrapper.appendChild(testimonials);

  // Vertical signature — decorative accent per vertical
  const vertical = getBlockRendererContext().vertical;

  if (vertical === 'saas' || vertical === 'tech') {
    wrapper.querySelectorAll('.pms-testimonial-item').forEach(item => {
      const dot = document.createElement('div');
      dot.className = 'pms-testimonial-verified-dot';
      item.prepend(dot);
    });
  }

  if (vertical === 'restaurant') {
    wrapper.querySelectorAll('.pms-testimonial-item').forEach(item => {
      const tag = document.createElement('div');
      tag.className = 'pms-testimonial-diner-tag';
      tag.textContent = 'REVIEW';
      item.prepend(tag);
    });
  }

  if (vertical === 'finance' || vertical === 'fintech') {
    wrapper.querySelectorAll('.pms-testimonial-item').forEach(item => {
      const badge = document.createElement('div');
      badge.className = 'pms-testimonial-trust-badge';
      badge.textContent = 'VERIFIED';
      item.prepend(badge);
    });
  }

  if (vertical === 'healthcare') {
    wrapper.querySelectorAll('.pms-testimonial-item').forEach(item => {
      const cross = document.createElement('div');
      cross.className = 'pms-testimonial-care-mark';
      item.prepend(cross);
    });
  }

  if (vertical === 'portfolio') {
    wrapper.querySelectorAll('.pms-testimonial-item').forEach(item => {
      const mark = document.createElement('div');
      mark.className = 'pms-testimonial-creative-mark';
      item.prepend(mark);
    });
  }

  if (vertical === 'agency') {
    wrapper.querySelectorAll('.pms-testimonial-item').forEach(item => {
      const badge = document.createElement('div');
      badge.className = 'pms-testimonial-campaign-badge';
      badge.textContent = 'CLIENT';
      item.prepend(badge);
    });
  }

  if (vertical === 'education') {
    wrapper.querySelectorAll('.pms-testimonial-item').forEach(item => {
      const tag = document.createElement('div');
      tag.className = 'pms-testimonial-student-tag';
      tag.textContent = 'STUDENT';
      item.prepend(tag);
    });
  }

  if (vertical === 'nonprofit') {
    wrapper.querySelectorAll('.pms-testimonial-item').forEach(item => {
      const tag = document.createElement('div');
      tag.className = 'pms-testimonial-supporter-tag';
      tag.textContent = 'SUPPORTER';
      item.prepend(tag);
    });
  }

  if (vertical === 'legal') {
    wrapper.querySelectorAll('.pms-testimonial-item').forEach(item => {
      const tag = document.createElement('div');
      tag.className = 'pms-testimonial-client-tag';
      tag.textContent = 'CLIENT';
      item.prepend(tag);
    });
  }

  if (vertical === 'ecommerce') {
    wrapper.querySelectorAll('.pms-testimonial-item').forEach(item => {
      const tag = document.createElement('div');
      tag.className = 'pms-testimonial-buyer-tag';
      tag.textContent = 'BUYER';
      item.prepend(tag);
    });
  }

  if (vertical === 'real_estate') {
    wrapper.querySelectorAll('.pms-testimonial-item').forEach(item => {
      const tag = document.createElement('div');
      tag.className = 'pms-testimonial-owner-tag';
      tag.textContent = 'HOMEOWNER';
      item.prepend(tag);
    });
  }

  if (vertical === 'automotive') {
    wrapper.querySelectorAll('.pms-testimonial-item').forEach(item => {
      const tag = document.createElement('div');
      tag.className = 'pms-testimonial-driver-tag';
      tag.textContent = 'DRIVER';
      item.prepend(tag);
    });
  }

  if (vertical === 'professional_services') {
    wrapper.querySelectorAll('.pms-testimonial-item').forEach(item => {
      const tag = document.createElement('div');
      tag.className = 'pms-testimonial-client-ps-tag';
      tag.textContent = 'CLIENT';
      item.prepend(tag);
    });
  }

  if (vertical === 'dental') {
    wrapper.querySelectorAll('.pms-testimonial-item').forEach(item => {
      const tag = document.createElement('div');
      tag.className = 'pms-testimonial-patient-tag';
      tag.textContent = 'PATIENT';
      item.prepend(tag);
    });
  }

  if (vertical === 'fun') {
    wrapper.querySelectorAll('.pms-testimonial-item').forEach(item => {
      const tag = document.createElement('div');
      tag.className = 'pms-testimonial-user-tag';
      tag.textContent = 'USER';
      item.prepend(tag);
    });
  }

  container.appendChild(wrapper);
});

// =============================================================================
// ALERT BLOCK
// =============================================================================

registerBlockRenderer('alert', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  const alertData = data as AlertData;
  const alertType = alertData.type ?? 'info';

  const wrapper = document.createElement('div');
  wrapper.className = `pms-block pms-block-alert pms-alert-${alertType}`;

  // Enhanced icon with wrapper for background
  let iconContent = '';
  switch (alertType) {
    case 'success':
      iconContent = iconCheck();
      break;
    case 'warning':
      iconContent = iconWarning();
      break;
    case 'promo':
      iconContent = iconCelebrate();
      break;
    default:
      iconContent = iconInfo();
  }

  let html = `<div class="pms-alert-icon-wrapper"><span class="pms-alert-icon">${iconContent}</span></div>`;

  html += '<div class="pms-alert-content">';
  if (alertData.title) {
    html += `<div class="pms-alert-title">${escapeHtml(alertData.title)}</div>`;
  }
  html += `<div class="pms-alert-message">${escapeHtml(alertData.message)}</div>`;
  html += '</div>';

  wrapper.innerHTML = html;

  if (alertData.action) {
    const btn = document.createElement('button');
    btn.className = 'pms-alert-action';
    btn.textContent = alertData.action.label;
    btn.addEventListener('click', () => {
      if (onAction) {
        onAction(alertData.action!.value);
      }
    });
    wrapper.appendChild(btn);
  }

  container.appendChild(wrapper);
});
